// ============================================================================
// GlobalPaywallGuard
// ----------------------------------------------------------------------------
// Hard-redirects users with an expired subscription to /pricing whenever they
// try to hit a "locked" route (dashboard, library, intervention flow, etc.).
//
// Why global instead of per-page? App Store Review Guideline 2.1 requires the
// expired demo account to land cleanly on the purchase flow no matter which
// route the reviewer opens first. A per-page useEffect (Dashboard.jsx) missed
// deep-links and library routes and left the reviewer on a blurred locked
// screen. This mounts once at the router root and enforces the rule
// everywhere.
// ============================================================================

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useBilling } from "../contexts/BillingContext";

// Route prefixes that require an ACTIVE / TRIALING / WAIVED entitlement.
// Anything not matching a prefix (e.g. /pricing, /account, /faq, /) is
// always accessible — those pages must stay reachable so the user can
// actually subscribe, manage their account, or read policies.
const LOCKED_PREFIXES = [
  "/dashboard",
  "/library",
  "/intervention",
  "/history",
  "/favorites",
  "/settings",
];

const isLockedPath = (pathname) =>
  LOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));

export const GlobalPaywallGuard = () => {
  const { user } = useAuth();
  const { billing, loading } = useBilling();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;              // wait until billing status has loaded
    if (!user) return;                // unauthenticated users hit /login already
    if (billing?.status !== "expired") return;
    if (!isLockedPath(location.pathname)) return;
    navigate("/pricing", { replace: true });
  }, [billing?.status, loading, user, location.pathname, navigate]);

  return null;
};

export default GlobalPaywallGuard;
